/**
 * GenericSliderDropdown - Card with popover slider and drag-to-increment
 *
 * Wraps the base Slider component with additional functionality:
 * - Card UI with label and value display
 * - Click-and-drag on card to increment/decrement values
 * - Popover containing slider + number input (stacked vertically)
 * - Configurable drag sensitivity
 * - Global keyboard shortcuts (active whether popover is open or closed)
 *
 * Architecture:
 * - This component handles: card UI, drag behavior, global keyboard bindings, popover
 * - Slider component handles: slider rendering, real-time snapping, number input
 *
 * Note: Keyboard bindings are handled at this level (not passed to inner Slider)
 * to ensure they work both when the popover is closed and when it's open.
 */

import * as Popover from '@radix-ui/react-popover';
import {Icon} from '@wandb/components';
import {SliderKeyboardOperation} from '@wandb/weave/common/util/media';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {changeSliderValue} from '../utils/stepUtils';
import {
  CARD_CLOSED,
  CARD_HEIGHT,
  CARD_OPEN_BOTTOM,
  CARD_PADDING,
  FIXED_SIZE,
  FLEX_COL,
  FLEX_CONTAINER,
  FLEX_ROW_CENTER,
} from './cardStyles';
import {Slider} from './Slider';
import {Title} from './Title';
import {SliderChangeSource} from './types';

export type SliderCardConfig = {
  /**
   * Pixels of horizontal drag required to increment/decrement by one unit
   * Default: 30
   */
  dragSensitivityPixels: number;
};

export type SliderCardKeyboardBindings = {
  [key: string]: SliderKeyboardOperation;
};

export type GenericSliderDropdownProps = {
  /** Display label for the slider */
  label: string;
  /** Current value */
  value: number;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Array of available values */
  availableValues: number[];
  /** Whether to snap to available values. Defaults to false. */
  snapToAvailableValues?: boolean;
  /** Stride length for keyboard/drag increments. Defaults to 1. */
  strideLength?: number;
  /** Callback when value changes */
  onChange: (value: number, source?: SliderChangeSource) => void;
  /** Optional keyboard bindings (e.g., {'Meta+ArrowRight': INCREMENT}) */
  keyboardBindings?: SliderCardKeyboardBindings;
  /** Optional CSS class name */
  className?: string;
  /** Optional configuration */
  config?: Partial<SliderCardConfig>;
  /** Test ID */
  'data-test'?: string;
  /** Layout mode for the slider:
   * - 'horizontal': Single line layout, no wrapping (slider and input side by side)
   * - 'vertical': Stacked layout (input above slider)
   * Defaults to 'horizontal'.
   */
  layout?: 'horizontal' | 'vertical';
};

const DEFAULT_CONFIG: SliderCardConfig = {
  dragSensitivityPixels: 30,
};

// ============================================================================
// Popover Styling
// ============================================================================

// When popover opens below (data-side="bottom"), card is at top connecting to content below
const POPOVER_CARD_BOTTOM = CARD_OPEN_BOTTOM;
const POPOVER_CONTENT_BOTTOM =
  'bg-wb-surface border !border-wb-border border-t-0 rounded-b-md';

// When popover opens above (data-side="top"), card is at bottom connecting to content above
const POPOVER_CARD_TOP_OVERRIDES =
  'group-data-[side=top]:!border-t-transparent group-data-[side=top]:!border-b-wb-border group-data-[side=top]:!rounded-t-none group-data-[side=top]:!rounded-b-md';
const POPOVER_CONTENT_TOP_OVERRIDES =
  'group-data-[side=top]:!border-t group-data-[side=top]:!border-b-0 group-data-[side=top]:rounded-b-none group-data-[side=top]:rounded-t-md';

// ============================================================================
// Internal Components
// ============================================================================

type SliderCardProps = {
  label: string;
  value: string;
  isDragging: boolean;
  isOpen?: boolean;
  showChevron?: boolean;
  chevronIcon?: 'chevron-down' | 'chevron-up';
  isInteractive?: boolean;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseUp?: () => void;
  onChevronClick?: () => void;
  className?: string;
};

const SliderCard = React.forwardRef<HTMLDivElement, SliderCardProps>(
  (
    {
      label,
      value,
      isOpen = false,
      showChevron = true,
      chevronIcon = 'chevron-down',
      isInteractive = true,
      onMouseDown,
      onMouseUp,
      onChevronClick,
      className,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        className={twMerge(
          CARD_CLOSED,
          CARD_HEIGHT,
          CARD_PADDING,
          FLEX_COL,
          'justify-center w-full',
          !isInteractive &&
            'cursor-default hover:bg-[light-dark(var(--moon-100),var(--moon-850))] hover:border-transparent hover:shadow-none',
          '!transition-none',
          isOpen && POPOVER_CARD_BOTTOM,
          isOpen && POPOVER_CARD_TOP_OVERRIDES,
          className
        )}>
        <div
          className={twMerge(
            FLEX_ROW_CENTER,
            'w-full min-w-0 overflow-hidden justify-between gap-8'
          )}>
          <div
            className={twMerge(
              FLEX_CONTAINER,
              'overflow-hidden',
              isInteractive ? 'cursor-grab' : 'cursor-default'
            )}>
            <Title label={label} value={value} labelPosition="separated" />
          </div>
          {showChevron && (
            <div
              onClick={e => {
                if (onChevronClick) {
                  e.stopPropagation();
                  onChevronClick();
                }
              }}
              className={twMerge(
                FIXED_SIZE,
                'text-wb-text-tertiary',
                onChevronClick
                  ? 'cursor-pointer visible'
                  : 'invisible group-hover:visible'
              )}>
              <Icon icon={chevronIcon} />
            </div>
          )}
        </div>
      </div>
    );
  }
);
SliderCard.displayName = 'SliderCard';

// ============================================================================
// Component Implementation
// ============================================================================
// Note: Shared styling constants (CARD_CLOSED, CARD_OPEN_BOTTOM, etc.) are
// imported from cardStyles.ts for consistency with GenericSelectorDropdown

export const GenericSliderDropdown: React.FC<GenericSliderDropdownProps> = ({
  label,
  value,
  min,
  max,
  availableValues,
  snapToAvailableValues = false,
  strideLength = 1,
  onChange: onChangeProp,
  keyboardBindings,
  className,
  config: userConfig,
  'data-test': dataTest = 'slider-card',
  layout,
}) => {
  const config = {...DEFAULT_CONFIG, ...userConfig};
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const dragStartX = useRef<number | null>(null);
  const dragAccumulator = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  /** True only after the user has moved far enough to change the value (not just mousedown + mouseup). */
  const hasDragged = useRef<boolean>(false);
  const effectiveLayout = isNarrow ? 'vertical' : (layout ?? 'horizontal');
  const isInteractive = availableValues.length > 1;

  // Calculate increment for dragging
  const calculateIncrement = useCallback(
    (deltaX: number): number => {
      if (snapToAvailableValues && availableValues.length > 0) {
        const pixelsPerStep = config.dragSensitivityPixels;
        return Math.floor(deltaX / pixelsPerStep);
      } else {
        const valueRange = max - min;
        const adjustedSensitivity = Math.max(
          config.dragSensitivityPixels,
          valueRange / 100
        );
        return Math.floor(deltaX / adjustedSensitivity);
      }
    },
    [
      snapToAvailableValues,
      config.dragSensitivityPixels,
      availableValues,
      min,
      max,
    ]
  );

  // Handle increment/decrement. `delta` is a detent count; changeSliderValue
  // multiplies it by the stride.
  const changeValue = useCallback(
    (delta: number, source: SliderChangeSource) => {
      const newValue = changeSliderValue(value, delta, {
        min,
        max,
        availableValues,
        snapToAvailableValues,
        stride: strideLength,
      });
      onChangeProp(newValue, source);
    },
    [
      value,
      min,
      max,
      availableValues,
      snapToAvailableValues,
      strideLength,
      onChangeProp,
    ]
  );

  // Mouse down handler - start drag tracking
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (open || availableValues.length <= 1) {
        return;
      } // Don't drag when popover is open or when there's only 1 value
      e.preventDefault(); // Prevent text selection from starting
      dragStartX.current = e.clientX;
      dragAccumulator.current = 0;
      hasDragged.current = false;
      setIsDragging(true);
    },
    [open, availableValues.length]
  );

  // Mouse up handler - open popover if no drag occurred. Removes overlay immediately.
  const handleMouseUp = useCallback(() => {
    const wasDragging = hasDragged.current;
    if (!wasDragging && !open && availableValues.length > 1) {
      setOpen(true);
    }
    setIsDragging(false);
    dragStartX.current = null;
    dragAccumulator.current = 0;

    // Prevent the synthetic click from the release from activating whatever is under the cursor
    if (wasDragging) {
      const preventClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener('click', preventClick, {
        capture: true,
        once: true,
      });
    }
  }, [open, availableValues.length]);

  // Track mouse movement
  useEffect(() => {
    if (!isDragging || dragStartX.current === null) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX.current!;
      dragAccumulator.current = deltaX;

      const increment = calculateIncrement(deltaX);

      if (increment !== 0) {
        hasDragged.current = true;
        changeValue(increment, SliderChangeSource.CardDrag);
        dragStartX.current = e.clientX;
        dragAccumulator.current = 0;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, calculateIncrement, changeValue, handleMouseUp]);

  // Global keyboard shortcuts - active whether popover is open or closed
  useEffect(() => {
    if (!keyboardBindings) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as Element;
      if (
        target &&
        ['input', 'textarea', 'select'].includes(target.tagName.toLowerCase())
      ) {
        return;
      }

      const modifiers: string[] = [];
      if (e.metaKey) {
        modifiers.push('Meta');
      }
      if (e.ctrlKey) {
        modifiers.push('Control');
      }
      if (e.altKey) {
        modifiers.push('Alt');
      }

      const combo = [...modifiers, e.key].join('+');
      const operation = keyboardBindings[combo];

      if (operation) {
        e.preventDefault();
        e.stopPropagation();
        // One keypress is one detent; changeValue applies the stride
        const delta = operation === SliderKeyboardOperation.INCREMENT ? 1 : -1;
        changeValue(delta, SliderChangeSource.Keyboard);
      }
    };

    // TODO: migrate configurable slider shortcuts to the inputSystem
    // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [keyboardBindings, changeValue]);

  // Watch popover content width and switch to vertical layout when narrow (< 400px)
  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      setIsNarrow(width < 400);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={twMerge('flex-1 w-full', CARD_HEIGHT, className)}
      data-test={dataTest}>
      {isDragging && (
        /* Full-screen overlay: blocks hover on other controls while dragging. */
        <div className="fixed inset-0 z-999999 cursor-grabbing pointer-events-auto" />
      )}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <SliderCard
            label={label}
            value={open ? '' : String(value)}
            isDragging={isDragging}
            isOpen={open}
            showChevron={isInteractive}
            chevronIcon="chevron-down"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            isInteractive={isInteractive}
          />
        </Popover.Trigger>

        <Popover.Portal>
          <TailwindContents>
            <Popover.Content
              align="end"
              side="bottom"
              sideOffset={-51}
              collisionPadding={10}
              className="group w-[var(--radix-popover-trigger-width)] min-w-[200px] flex flex-col data-[side=top]:flex-col-reverse shadow-md"
              onOpenAutoFocus={e => e.preventDefault()}>
              <SliderCard
                label={label}
                value=""
                isDragging={false}
                isOpen={true}
                chevronIcon="chevron-up"
                isInteractive={true}
                onMouseDown={() => {}}
                onChevronClick={() => setOpen(false)}
                className="!h-[32px] !py-4 flex-shrink-0"
              />

              <div
                className={twMerge(
                  POPOVER_CONTENT_BOTTOM,
                  POPOVER_CONTENT_TOP_OVERRIDES,
                  'flex-1 flex flex-col'
                )}>
                <div className="flex flex-col gap-8 p-12 items-center justify-center flex-1">
                  <Slider
                    min={min}
                    max={max}
                    current={value}
                    values={availableValues}
                    snapToExistingValue={snapToAvailableValues}
                    strideLength={strideLength}
                    onChange={onChangeProp}
                    showLabel={false}
                    showInput={true}
                    keyboardBindings={undefined}
                    className="flex-col items-center w-full"
                    data-test={dataTest}
                    layout={effectiveLayout}
                  />
                </div>
              </div>
            </Popover.Content>
          </TailwindContents>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
